import { errorHandler, performanceMonitor } from './errorHandler';

// WebSocket Message Types
export interface StartStreamMessage {
  type: 'start_stream';
  roomId: string;
}

export interface AudioChunkMessage {
  type: 'audio_chunk';
  data: ArrayBuffer;
  timestamp: number;
  sequenceId: number;
}

export interface JoinRoomMessage {
  type: 'join_room';
  roomId: string;
}

export interface TranscriptionMessage {
  type: 'transcription';
  text: string;
  timestamp: number;
  confidence: number;
}

export interface StatusMessage {
  type: 'status';
  status: 'connected' | 'streaming' | 'error' | 'disconnected';
  message?: string;
}

export type ClientMessage = StartStreamMessage | AudioChunkMessage | JoinRoomMessage;
export type ServerMessage = TranscriptionMessage | StatusMessage;

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'reconnecting' | 'error';

export interface WebSocketClientOptions {
  url: string;
  maxReconnectAttempts?: number;
  initialReconnectDelay?: number;
  maxReconnectDelay?: number;
  reconnectBackoffFactor?: number;
}

export interface WebSocketClientEvents {
  onStatusChange: (status: ConnectionStatus) => void;
  onTranscription: (message: TranscriptionMessage) => void;
  onError: (error: Error) => void;
  onMessage: (message: ServerMessage) => void;
}

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private url: string;
  private status: ConnectionStatus = 'disconnected';
  private reconnectAttempts = 0;
  private maxReconnectAttempts: number;
  private initialReconnectDelay: number;
  private maxReconnectDelay: number;
  private reconnectBackoffFactor: number;
  private reconnectTimeoutId: number | null = null;
  private events: Partial<WebSocketClientEvents> = {};

  constructor(options: WebSocketClientOptions) {
    this.url = options.url;
    this.maxReconnectAttempts = options.maxReconnectAttempts ?? 10;
    this.initialReconnectDelay = options.initialReconnectDelay ?? 1000;
    this.maxReconnectDelay = options.maxReconnectDelay ?? 30000;
    this.reconnectBackoffFactor = options.reconnectBackoffFactor ?? 2;
  }

  // Event subscription methods
  on<K extends keyof WebSocketClientEvents>(event: K, handler: WebSocketClientEvents[K]): void {
    this.events[event] = handler;
  }

  off<K extends keyof WebSocketClientEvents>(event: K): void {
    delete this.events[event];
  }



  // Connection management
  connect(): Promise<void> {
    const endTimer = performanceMonitor.startTimer('websocket_connect');
    
    return new Promise((resolve, reject) => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        endTimer();
        resolve();
        return;
      }

      this.updateStatus('connecting');
      
      try {
        this.ws = new WebSocket(this.url);
        
        this.ws.onopen = () => {
          const duration = endTimer();
          console.log(`WebSocket connected in ${duration.toFixed(2)}ms`);
          
          this.updateStatus('connected');
          this.reconnectAttempts = 0;
          this.clearReconnectTimeout();
          resolve();
        };

        this.ws.onmessage = (event) => {
          this.handleMessage(event);
        };

        this.ws.onclose = (event) => {
          this.handleClose(event);
        };

        this.ws.onerror = (event) => {
          endTimer();
          const error = new Error(`WebSocket error: ${event.type}`);
          this.handleError(error);
          reject(error);
        };

      } catch (error) {
        endTimer();
        this.handleError(error as Error);
        reject(error);
      }
    });
  }

  disconnect(): void {
    this.clearReconnectTimeout();
    
    if (this.ws) {
      this.ws.onclose = null; // Prevent reconnection on manual disconnect
      this.ws.close();
      this.ws = null;
    }
    
    this.updateStatus('disconnected');
  }

  // Message sending methods
  sendMessage(message: ClientMessage): boolean {
    if (!this.isConnected() || !this.ws) {
      this.handleError(new Error('Cannot send message: WebSocket not connected'));
      return false;
    }

    try {
      if (message.type === 'audio_chunk') {
        // Handle binary audio data separately
        return this.sendAudioChunk(message as AudioChunkMessage);
      } else {
        // Send JSON messages
        this.ws.send(JSON.stringify(message));
        return true;
      }
    } catch (error) {
      this.handleError(error as Error);
      return false;
    }
  }

  sendAudioChunk(message: AudioChunkMessage): boolean {
    if (!this.isConnected() || !this.ws) {
      this.handleError(new Error('Cannot send audio chunk: WebSocket not connected'));
      return false;
    }

    try {
      // Create a message with metadata and binary data
      const metadata = {
        type: message.type,
        timestamp: message.timestamp,
        sequenceId: message.sequenceId
      };
      
      // Send metadata as JSON followed by binary data
      // In a real implementation, you might want to use a more sophisticated protocol
      // For now, we'll send as a single message with base64 encoded data
      const audioBase64 = this.arrayBufferToBase64(message.data);
      const fullMessage = {
        ...metadata,
        data: audioBase64
      };
      
      this.ws.send(JSON.stringify(fullMessage));
      return true;
    } catch (error) {
      this.handleError(error as Error);
      return false;
    }
  }

  // Utility methods
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  getStatus(): ConnectionStatus {
    return this.status;
  }

  private updateStatus(status: ConnectionStatus): void {
    if (this.status !== status) {
      this.status = status;
      this.events.onStatusChange?.(status);
    }
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const message: ServerMessage = JSON.parse(event.data);
      
      // Emit specific event based on message type
      if (message.type === 'transcription') {
        this.events.onTranscription?.(message as TranscriptionMessage);
      }
      
      // Emit general message event
      this.events.onMessage?.(message);
      
    } catch (error) {
      this.handleError(new Error(`Failed to parse message: ${error}`));
    }
  }

  private handleClose(event: CloseEvent): void {
    this.ws = null;
    
    if (event.wasClean) {
      this.updateStatus('disconnected');
    } else {
      this.updateStatus('disconnected');
      this.attemptReconnect();
    }
  }

  private handleError(error: Error): void {
    this.updateStatus('error');
    this.events.onError?.(error);
    
    // Log to error handler
    errorHandler.handleWebSocketError(error, {
      component: 'WebSocketClient',
      operation: 'websocket_operation',
      metadata: {
        url: this.url,
        readyState: this.ws?.readyState,
        reconnectAttempts: this.reconnectAttempts
      }
    });
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.handleError(new Error('Max reconnection attempts reached'));
      return;
    }

    this.updateStatus('reconnecting');
    this.reconnectAttempts++;

    const delay = Math.min(
      this.initialReconnectDelay * Math.pow(this.reconnectBackoffFactor, this.reconnectAttempts - 1),
      this.maxReconnectDelay
    );

    this.reconnectTimeoutId = window.setTimeout(() => {
      this.connect().catch((error) => {
        console.error('Reconnection failed:', error);
        this.attemptReconnect();
      });
    }, delay);
  }

  private clearReconnectTimeout(): void {
    if (this.reconnectTimeoutId !== null) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      const byte = bytes[i];
      if (byte !== undefined) {
        binary += String.fromCharCode(byte);
      }
    }
    return btoa(binary);
  }
}